// WebGL ripple shader — adapted from the user-provided sketch.
// Two modes:
//   - One-shot pulse (initial click feedback)
//   - Sustained ripples (loading state while generation runs)

const VERT_SRC = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`

const FRAG_SRC = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_texture;
uniform vec2 u_center;
uniform vec2 u_resolution;
uniform vec3 u_waveProgress;
uniform vec3 u_waveStrength;
uniform float u_centerLens;
uniform float u_opacity;

float rippleBand(float dist, float progress, float maxRadius, float width, float amplitude) {
  float active = step(0.0001, progress);
  float fade = (1.0 - smoothstep(0.0, 1.0, progress)) * active;
  float front = maxRadius * pow(progress, 1.2);
  return exp(-pow((dist - front) * width, 2.0)) * amplitude * fade;
}

float activeWaveStrength(float progress, float strength) {
  return strength * step(0.0, progress) * (1.0 - step(1.0, progress));
}

void main() {
  float aspect = u_resolution.x / max(u_resolution.y, 1.0);
  vec2 delta = v_uv - u_center;
  vec2 rippleDelta = vec2(delta.x * aspect, delta.y);
  float dist = length(rippleDelta);

  float s0 = activeWaveStrength(u_waveProgress.x, u_waveStrength.x);
  float s1 = activeWaveStrength(u_waveProgress.y, u_waveStrength.y);
  float s2 = activeWaveStrength(u_waveProgress.z, u_waveStrength.z);

  float b0 = rippleBand(dist, clamp(u_waveProgress.x, 0.0, 1.0), 0.25, 30.0, s0);
  float b1 = rippleBand(dist, clamp(u_waveProgress.y, 0.0, 1.0), 0.25, 30.0, s1);
  float b2 = rippleBand(dist, clamp(u_waveProgress.z, 0.0, 1.0), 0.25, 30.0, s2);
  float band = b0 + b1 + b2;

  float leadStrength = max(s0, max(s1, s2));
  float leadProgress = max(u_waveProgress.x, max(u_waveProgress.y, u_waveProgress.z));

  float centerAlpha = exp(-pow(dist * 22.0, 2.0))
    * (1.0 - smoothstep(0.0, 0.18, max(leadProgress, 0.0)))
    * (0.05 * leadStrength);

  float dropletCore = exp(-pow(dist * 15.0, 2.0));
  float dropletRim  = exp(-pow((dist - 0.048) * 72.0, 2.0));

  vec2 dir = dist > 0.0001 ? rippleDelta / dist : vec2(0.0);
  dir.x /= max(aspect, 0.0001);

  float shimmer = 0.72 + 0.28 * sin(dist * 62.0 - max(leadProgress, 0.0) * 18.0);
  float offset = band * 0.006;

  float centerLift = exp(-pow(dist * 24.0, 2.0))
    * (1.0 - smoothstep(0.0, 0.16, max(leadProgress, 0.0)))
    * (0.0018 * leadStrength);
  float dropletOffset = u_centerLens * ((dropletRim * 0.0031) - (dropletCore * 0.0017));

  vec2 displacedUv = clamp(
    v_uv + dir * (centerLift + offset * shimmer + dropletOffset),
    0.001, 0.999
  );

  vec4 baseColor      = texture2D(u_texture, v_uv);
  vec4 displacedColor = texture2D(u_texture, displacedUv);

  float dropletMask = u_centerLens * (dropletCore * 0.42 + dropletRim * 0.18);
  float distortionStrength = clamp(
    (band * 1.45 + centerAlpha * 0.95 + dropletMask) * u_opacity,
    0.0, 0.82
  );

  float coverage = sqrt(distortionStrength);
  gl_FragColor = vec4(mix(baseColor.rgb, displacedColor.rgb, coverage), coverage);
}`

interface GLCtx {
  gl: WebGLRenderingContext
  program: WebGLProgram
  texture: WebGLTexture
  uniforms: {
    center: WebGLUniformLocation | null
    resolution: WebGLUniformLocation | null
    waveProgress: WebGLUniformLocation | null
    waveStrength: WebGLUniformLocation | null
    centerLens: WebGLUniformLocation | null
    opacity: WebGLUniformLocation | null
  }
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!
  gl.shaderSource(s, src)
  gl.compileShader(s)
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s)
    throw new Error('shader compile error: ' + log)
  }
  return s
}

function initGL(canvas: HTMLCanvasElement): GLCtx | null {
  const gl = (canvas.getContext('webgl', {
    alpha: true,
    antialias: true,
    premultipliedAlpha: false,
  }) || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null
  if (!gl) return null

  const program = gl.createProgram()!
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERT_SRC))
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAG_SRC))
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error('program link error: ' + gl.getProgramInfoLog(program))
  }
  gl.useProgram(program)

  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)

  const loc = gl.getAttribLocation(program, 'a_position')
  gl.enableVertexAttribArray(loc)
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

  gl.enable(gl.BLEND)
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
  gl.clearColor(0, 0, 0, 0)

  const texture = gl.createTexture()!
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.uniform1i(gl.getUniformLocation(program, 'u_texture'), 0)

  return {
    gl,
    program,
    texture,
    uniforms: {
      center: gl.getUniformLocation(program, 'u_center'),
      resolution: gl.getUniformLocation(program, 'u_resolution'),
      waveProgress: gl.getUniformLocation(program, 'u_waveProgress'),
      waveStrength: gl.getUniformLocation(program, 'u_waveStrength'),
      centerLens: gl.getUniformLocation(program, 'u_centerLens'),
      opacity: gl.getUniformLocation(program, 'u_opacity'),
    },
  }
}

const INITIAL_DURATION = 1800
const WAVE_DURATION = 1300
const WAVE_INTERVAL = 1000
const INITIAL_STRENGTH = 1.0
const SUSTAINED_STRENGTH = 0.72

export class Ripple {
  private canvas: HTMLCanvasElement
  private img: HTMLImageElement
  private ctx: GLCtx | null = null
  private rafId: number | null = null
  private uploaded = false
  // 每次 start() 拿一个递增 token；旧 ripple 的 stopper 在 token 不匹配时直接 no-op，
  // 防止它在新 ripple 已经接管时去 cancelRaf / fadeOut。
  private currentToken = 0

  constructor(canvas: HTMLCanvasElement, img: HTMLImageElement) {
    this.canvas = canvas
    this.img = img
    try {
      this.ctx = initGL(canvas)
    } catch (e) {
      console.warn('ripple: webgl init failed, click effect disabled', e)
      this.ctx = null
    }

    const onLoad = () => this.uploadTexture()
    img.addEventListener('load', onLoad)
    if (img.complete && img.naturalWidth > 0) onLoad()
  }

  private syncSize() {
    if (!this.ctx) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.canvas.width = Math.round(this.canvas.clientWidth * dpr)
    this.canvas.height = Math.round(this.canvas.clientHeight * dpr)
    this.ctx.gl.viewport(0, 0, this.canvas.width, this.canvas.height)
  }

  private uploadTexture() {
    if (!this.ctx) return
    const { gl, texture } = this.ctx
    this.syncSize()
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.img)
      this.uploaded = true
    } catch (e) {
      // CORS-tainted image (e.g. cross-origin without crossOrigin attr) — disable shader
      console.warn('ripple: texture upload failed', e)
      this.uploaded = false
    }
  }

  /**
   * Trigger a ripple at normalized (nx, ny) ∈ [0,1].
   * sustained=true keeps the wave looping until stop() is called.
   * Returns a stopper function.
   */
  start(nx: number, ny: number, sustained: boolean): () => void {
    if (!this.ctx || !this.uploaded) {
      // Graceful no-op
      return () => {}
    }
    // 这次 start 的 token；新 start 会自增 token，让旧 stopper 失效
    const myToken = ++this.currentToken

    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.canvas.style.opacity = '1'
    this.syncSize()

    const t0 = performance.now()
    const ctx = this.ctx
    let stopped = false

    const frame = (now: number) => {
      // 旧 ripple：要么是这次被 stop 了，要么是新 start 已经接管 — 都不画
      if (stopped || myToken !== this.currentToken) return
      const elapsed = now - t0
      const s = Math.min(elapsed / INITIAL_DURATION, 1)
      const going = s < 1

      const extra = Math.max(0, elapsed - INITIAL_DURATION)
      const wi = sustained ? Math.floor(extra / WAVE_INTERVAL) : -1
      const p0 = wi >= 0 ? extra - wi * WAVE_INTERVAL : -1
      const p1 = wi >= 1 ? extra - (wi - 1) * WAVE_INTERVAL : -1
      const p2 = wi >= 2 ? extra - (wi - 2) * WAVE_INTERVAL : -1
      const w0 = p0 >= 0 && p0 <= WAVE_DURATION ? p0 / WAVE_DURATION : -1
      const w1 = p1 >= 0 && p1 <= WAVE_DURATION ? p1 / WAVE_DURATION : -1
      const w2 = p2 >= 0 && p2 <= WAVE_DURATION ? p2 / WAVE_DURATION : -1
      const str = sustained ? SUSTAINED_STRENGTH : 0

      const { gl, uniforms, program } = ctx
      gl.useProgram(program)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.uniform2f(uniforms.center!, nx, 1 - ny)
      gl.uniform2f(uniforms.resolution!, this.canvas.width, this.canvas.height)
      gl.uniform3f(uniforms.waveProgress!, going ? s : w0, going ? -1 : w1, going ? -1 : w2)
      gl.uniform3f(uniforms.waveStrength!, going ? INITIAL_STRENGTH : str, going ? 0 : str, going ? 0 : str)
      gl.uniform1f(uniforms.centerLens!, going ? 0.96 : 0)
      gl.uniform1f(uniforms.opacity!, 0.72)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

      if (going || (sustained && wi >= 0)) {
        this.rafId = requestAnimationFrame(frame)
      } else {
        if (myToken === this.currentToken) this.fadeOut()
      }
    }

    this.rafId = requestAnimationFrame(frame)

    return () => {
      // 旧 stopper 调用时，新 ripple 已经接管 — 直接退出，别动新 ripple 的 rafId / opacity
      if (myToken !== this.currentToken) return
      stopped = true
      if (this.rafId) cancelAnimationFrame(this.rafId)
      this.rafId = null
      this.fadeOut()
    }
  }

  private fadeOut() {
    this.canvas.style.opacity = '0'
  }
}
